import { useEffect, useState, useCallback, useRef } from 'react';

import { buildAssetUrl } from "utils/Assets";
import { isCanvasModified } from 'utils/Refinements';

/**
 *
 * This hook returns data urls of the refined image based on the following parameter object
 * urls for unrefined images and updated textvalues for text variables.
 * and since this is asynchronouse returns object with isLoading, refinementDataUrl,
 * and error attributes.
 * @param values - object defining the current values of the form
 * @param initialValues - object defining the initial values of the form
 * @param libraryId - libraryId of the asset
 * @param skip - boolean to skip the hook (This is a pattern to avoid conditional call of hooks)
 * @returns Object with isLoading, refinedDataMap, and error attributes.
 */
export default function useRefinedData(values, initialValues, libraryId, skip, forceInitialCrop = false) {
	const TWO = 2;
	const [isLoading, setIsLoading] = useState(!skip);
	const [error, setError] = useState([]);
	const [initialCrop, setInitialCrop] = useState(forceInitialCrop);
	const refinedDataMap = useRef(new Map());
	const loadingItems = useRef(new Set());

	const drawCanvas = useCallback((
		canvas,
		img,
		ctx,
		top,
		left,
		frameWidth,
		frameHeight,
		canvasWidth
	) => {
		const { width } = img;
		const zoom = canvasWidth / width;
		const focalX = (left + frameWidth / TWO) / zoom;
		const focalY = (top + frameHeight / TWO) / zoom;

		canvas.width = frameWidth;
		canvas.height = frameHeight;

		ctx.drawImage(
			img,
			focalX - frameWidth / TWO / zoom,
			focalY - frameHeight / TWO / zoom,
			frameWidth / zoom,
			frameHeight / zoom,
			0,
			0,
			frameWidth,
			frameHeight
		);

		return canvas.toDataURL();
	}, []);

	const handleLoad = useCallback((
		name,
		canvas,
		img,
		ctx,
		top,
		left,
		frameWidth,
		frameHeight,
		canvasWidth
	) => {
		const refinementDataUrl = drawCanvas(
			canvas,
			img,
			ctx,
			top,
			left,
			frameWidth,
			frameHeight,
			canvasWidth
		);

		refinedDataMap.current.set(name, { refinementDataUrl });
		loadingItems.current.delete(name);
		setIsLoading(loadingItems.current.size > 0);
	}, []);

	const handleError = useCallback((name, error) => {
		setError(prev => [...prev, error]);
		loadingItems.current.delete(name);
		setIsLoading(loadingItems.current.size > 0);
	}, []);

	useEffect(() => {
		if (skip) {
			return;
		}

		for (const key in values) {
			const {
				name,
				data,
				prop,
				type,
				cropMetadata = {},
			} = values[key];
			if (type === "Image") {
				const {
					frame,
					canvas,
				} = cropMetadata;
				const {
					width: frameWidth,
					height: frameHeight,
				} = frame??{};
				const {
					left,
					top,
					width: canvasWidth,
					height: canvasHeight,
				} = canvas??{};
				const initialCanvas = initialValues[key].cropMetadata?.canvas;
				const initialProp = initialValues[key].prop;
				const isCropDirty = canvasWidth && canvasHeight
				&& (isCanvasModified(canvas, initialCanvas) || prop !== initialProp || initialCrop);
				if (isCropDirty) {
					loadingItems.current.add(name);
					const canvas = document.createElement('canvas');
					const img = new Image();
					const ctx = canvas.getContext('2d');
					// Build original asset url
					const assetUrl = buildAssetUrl(libraryId, data);

					img.crossOrigin = "use-credentials";
					img.onload = () => handleLoad(name, canvas, img, ctx, top, left, frameWidth, frameHeight, canvasWidth);
					img.onerror = e => handleError(name, e);

					img.src = assetUrl;
					setInitialCrop(false);
				} else {
					refinedDataMap.current.set(name, { refinementDataUrl: prop });
				}
			} else {
				refinedDataMap.current.set(name, { text: data });
			}
		}
		setIsLoading(loadingItems.current.size > 0);
	}, [values, skip, handleLoad, handleError, initialCrop]);

	return { isLoading, refinedDataMap: refinedDataMap.current, error };
}
